import { FormEvent, KeyboardEvent, useEffect, useRef } from 'react';
import Icon from '@amzn/meridian/icon';
import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import closeIconTokens from '@amzn/meridian-tokens/base/icon/close-small';
import searchIconTokens from '@amzn/meridian-tokens/base/icon/search';
import { css } from '@emotion/css';
import useActionButtonStyles from '../hooks/useActionButtonStyles';
import useSurfaceStyles from '../hooks/useSurfaceStyles';
import { createTransition, motionDurations } from '../theme/motion';

const FOCUS_SHORTCUT_KEY = '/';
const COMPACT_LAYOUT_QUERY = '@media (max-width: 560px)';
// Autofocus only with a mouse or trackpad, so touch devices don't pop the on-screen keyboard on load.
const PRECISE_POINTER_QUERY = '(pointer: fine)';

interface HeroSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onClear: () => void;
}

function isEditableElement(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

export default function HeroSearchBar({ value, onChange, onSubmit, onClear }: HeroSearchBarProps) {
  const meridianTheme = useMeridianTheme();
  const { accentRingColor, fieldClass } = useSurfaceStyles();
  const { primaryButtonClass } = useActionButtonStyles();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isDarkMode = meridianTheme.mode === 'dark';
  const restingShadow = isDarkMode
    ? '0 1px 2px rgba(0, 0, 0, 0.3), 0 12px 32px -12px rgba(0, 0, 0, 0.6)'
    : '0 1px 2px rgba(2, 8, 14, 0.04), 0 12px 32px -12px rgba(2, 8, 14, 0.18)';
  const focusedShadow = isDarkMode
    ? `0 0 0 4px ${accentRingColor}, 0 16px 40px -12px rgba(0, 0, 0, 0.7)`
    : `0 0 0 4px ${accentRingColor}, 0 16px 40px -12px rgba(2, 8, 14, 0.22)`;

  useEffect(() => {
    const focusOnShortcut = (event: globalThis.KeyboardEvent) => {
      if (event.key !== FOCUS_SHORTCUT_KEY || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isEditableElement(event.target)) return;
      event.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener('keydown', focusOnShortcut);
    return () => window.removeEventListener('keydown', focusOnShortcut);
  }, []);

  useEffect(() => {
    if (window.matchMedia(PRECISE_POINTER_QUERY).matches) inputRef.current?.focus();
  }, []);

  const formClass = css(fieldClass, {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    height: 60,
    padding: '6px 6px 6px 22px',
    borderRadius: 16,
    boxShadow: restingShadow,
    '&:focus-within': { boxShadow: focusedShadow },
  });
  const inputClass = css({
    flex: '1 1 auto',
    minWidth: 0,
    height: '100%',
    padding: '0 4px',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: 'inherit',
    fontFamily: 'inherit',
    fontSize: 17,
    letterSpacing: '-0.006em',
    '&::-webkit-search-cancel-button': {
      WebkitAppearance: 'none',
    },
  });
  const shortcutHintClass = css({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 22,
    height: 22,
    padding: '0 6px',
    boxSizing: 'border-box',
    borderRadius: 6,
    border: `1px solid ${meridianTheme('themeBorderSubdued')}`,
    backgroundColor: meridianTheme('themePageSubdued'),
    color: meridianTheme('themeInkSubdued'),
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 500,
    [`.${formClass}:focus-within &`]: { opacity: 0 },
    transition: createTransition(['opacity']),
    [COMPACT_LAYOUT_QUERY]: { display: 'none' },
  });
  const clearButtonClass = css({
    display: 'flex',
    flex: '0 0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    padding: 0,
    border: 0,
    borderRadius: 999,
    backgroundColor: 'transparent',
    color: meridianTheme('themeInkSubdued'),
    cursor: 'pointer',
    transition: createTransition(['background-color', 'color'], motionDurations.instant),
    '&:hover': {
      backgroundColor: meridianTheme('themeActionNeutralHover'),
      color: meridianTheme('themeInkDefault'),
    },
    '&:focus-visible': {
      outline: `2px solid ${meridianTheme('themeBorderFocus')}`,
      outlineOffset: 1,
    },
  });
  const submitButtonClass = css(primaryButtonClass, {
    width: 46,
    height: 46,
    padding: 0,
    borderRadius: 12,
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && value) {
      event.preventDefault();
      onClear();
    }
  };

  const handleClear = () => {
    onClear();
    inputRef.current?.focus();
  };

  return (
    <form role="search" className={formClass} onSubmit={handleSubmit}>
      <input
        ref={inputRef}
        type="search"
        className={inputClass}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleInputKeyDown}
        aria-label="Search services"
        placeholder="Search by service name"
        autoComplete="off"
        spellCheck={false}
      />
      {value ? (
        <button type="button" className={clearButtonClass} onClick={handleClear} aria-label="Clear search">
          <Icon tokens={closeIconTokens}>Clear search</Icon>
        </button>
      ) : (
        <kbd className={shortcutHintClass} aria-hidden="true">
          {FOCUS_SHORTCUT_KEY}
        </kbd>
      )}
      <button type="submit" className={submitButtonClass} aria-label="Search" title="Search">
        <Icon tokens={searchIconTokens} />
      </button>
    </form>
  );
}
